import React, { useEffect, useState } from "react";
import "./kioskSelectPlayer.scss";
import { useTranslation } from "react-i18next";
import { ButtonNew as Button } from "components/buttonNew";
import { IBooking } from "redux/reducers/models/teetime";
import { ICustomer } from "redux/reducers/models/customer";
import { KioskHelpFooter } from "./KioskHelpFooter";
import Checkbox from "components/form/checkbox/Checkbox";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle } from "@fortawesome/pro-regular-svg-icons";

interface IKioskSelectPlayerProps {
  booking: IBooking | null;
  customer: Partial<ICustomer> | null;
  onSelectCustomer: (customer: Partial<ICustomer>) => void;
  onContinue: () => void;
  onBack: () => void;
}

export function KioskSelectPlayer({
  booking,
  customer,
  onSelectCustomer,
  onContinue,
  onBack,
}: IKioskSelectPlayerProps) {
  const { t } = useTranslation();
  const [selectedParticipantId, setSelectedParticipantId] = useState<number | null>(null);

  useEffect(() => {
    if (!booking?.participants || booking.participants.length === 0) {
      setSelectedParticipantId(null);
      return;
    }

    // If the user has already selected a participant, don't override their choice.
    if (selectedParticipantId !== null) {
      return;
    }

    // Prefer the current customer only if they are not already checked-in
    const currentParticipant = booking.participants.find(
      (participant: { customer_id: number; check_in_status: string; guest?: boolean }) =>
        !participant.guest && participant.customer_id === customer?.id && participant.check_in_status !== "checked",
    );

    if (currentParticipant) {
      setSelectedParticipantId(currentParticipant.id);

      const selectedCustomer: Partial<ICustomer> = {
        ...(currentParticipant as any).customer,
        id: (currentParticipant as any).customer_id,
        full_name: (currentParticipant as any).full_name,
        first_name: (currentParticipant as any).first_name,
        last_name: (currentParticipant as any).last_name,
      };

      onSelectCustomer(selectedCustomer);
      return;
    }

    // Otherwise, choose the first non-guest participant who is not already checked-in
    const firstAvailableParticipant = booking.participants.find(
      (participant: { check_in_status: string; guest?: boolean }) =>
        !participant.guest && participant.check_in_status !== "checked",
    );

    if (firstAvailableParticipant) {
      setSelectedParticipantId(firstAvailableParticipant.id);

      const selectedCustomer: Partial<ICustomer> = {
        ...(firstAvailableParticipant as any).customer,
        id: (firstAvailableParticipant as any).customer_id,
        full_name: (firstAvailableParticipant as any).full_name,
        first_name: (firstAvailableParticipant as any).first_name,
        last_name: (firstAvailableParticipant as any).last_name,
      };

      onSelectCustomer(selectedCustomer);
      return;
    }

    // All participants are checked-in: nothing selected, Continue stays disabled
    setSelectedParticipantId(null);
  }, [booking, customer, onSelectCustomer, selectedParticipantId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      onBack();
    }, 25000);

    return () => clearTimeout(timer);
  }, [onBack]);

  if (!booking) {
    return null;
  }

  const handleParticipantClick = (participant: {
    id: number;
    customer_id: number;
    full_name: string;
    first_name?: string;
    last_name?: string;
    check_in_status: string;
    guest?: boolean;
    customer?: ICustomer;
  }) => {

    if (participant.check_in_status === "checked") {
      return;
    }

    setSelectedParticipantId(participant.id);

    const selectedCustomer: Partial<ICustomer> = {
      ...participant.customer,
      id: participant.customer_id,
      full_name: participant.full_name,
      first_name: participant.first_name,
      last_name: participant.last_name,
    };

    onSelectCustomer(selectedCustomer);
  };

  const handleContinue = () => {
    if (!selectedParticipantId) {
      return;
    }

    onContinue();
  };

  return (
    <div className="ui-kiosk-container">
      <img src="../../../../public/images/Tee-On.png" alt="teeon.com" className="ui-kiosk-logo" />

      <p className="ui-kiosk-title">{t("Choose a player")}</p>
      <p className="ui-kiosk-subtitle">{t("Please select a player from this booking.")}</p>

      <div className="ui-kiosk-select-player-list">
        {booking?.participants
          ?.filter(
            (participant: { guest?: boolean }) => !participant.guest,
          )
          .map(
          (
            participant: {
              id: number;
              customer_id: number;
              full_name: string;
              initials?: string;
              first_name?: string;
              last_name?: string;
              guest?: boolean;
              check_in_status: string;
              customer?: ICustomer;
            },
            index: number,
          ) => {
            const isCheckedIn = participant?.check_in_status === "checked";
            const isSelected = selectedParticipantId === participant.id;
            const initials = participant?.initials || participant?.full_name?.slice(0, 2) || "";

            return (
              <button
                key={index}
                type="button"
                className={`ui-kiosk-select-player-pill ${isSelected ? "ui-kiosk-select-player-pill--active" : ""} ${
                  isCheckedIn ? "ui-kiosk-select-player-pill--disabled" : ""
                }`}
                onClick={() => handleParticipantClick(participant)}
                disabled={isCheckedIn}
              >
                <div className="ui-kiosk-select-player-pill-left">
                  <div className="ui-kiosk-select-player-pill-avatar">
                    <span className="ui-kiosk-select-player-pill-initials">{initials}</span>
                  </div>
                  <span className="ui-kiosk-select-player-pill-name">
                    {participant.full_name}
                    {/* {participant?.guest ? ` (${t("G")})` : ""} */}
                    {isCheckedIn ? <FontAwesomeIcon icon={faCheckCircle} style={{ color: "#039855" }} /> : ""}


                  </span>
                </div>
                <Checkbox checked={isSelected} disabled={isCheckedIn} circle size="small" readOnly={isCheckedIn} />
              </button>
            );
          },
        )}
      </div>

      <div className="ui-kiosk-button-container">
        <Button onClick={handleContinue} type="primary" className="ui-kiosk-button" disabled={!selectedParticipantId}>
          {t("Continue")}
        </Button>

        <Button onClick={onBack} type="secondary" className="ui-kiosk-button ml-0">
          {t("Go back")}
        </Button>

        <KioskHelpFooter />
      </div>
    </div>
  );
}
